import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { format } from 'date-fns';

import { Painel } from '../../../../components/Painel';
import { Icon } from '../../../../components/Icon';

import { api } from '../../../../services/api';
import { colors } from '../../../../styles/tokens';
import { useToast } from '../../../../hooks/toast';
import { formatName } from '../../../../utils/formatName';
import { currencyMask } from '../../../../utils/inputMasks';

import noAvatar from '../../../../assets/no-avatar.svg';

import {
  ActionsRow,
  BackLink,
  Card,
  CardTitle,
  Container,
  ContainerAvatar,
  EmptyState,
  Field,
  FieldInput,
  FieldInputGroup,
  FieldsRow,
  FieldUnit,
  Header,
  HistoryRow,
  PrimaryButton,
  SecondaryButton,
  StatusBanner,
  StudentName,
  Subtitle,
} from './styles';

interface IUserProps {
  fullname: string;
  avatar_url?: string;
}

interface IMembership {
  id: string;
  amount: number;
  due_day: number;
  active: boolean;
  created_at: string;
}

interface IPayment {
  id: string;
  amount: number;
  competence_date: string;
  paid_at: string;
}

const UserDues: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { addToast } = useToast();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [marking, setMarking] = useState(false);
  const [user, setUser] = useState<IUserProps>();
  const [membership, setMembership] = useState<IMembership | null>(null);
  const [payments, setPayments] = useState<IPayment[]>([]);
  const [amount, setAmount] = useState('');
  const [dueDay, setDueDay] = useState('');

  const getUserData = useCallback(() => {
    api
      .get(`users/gym/${id}`)
      .then(response => setUser(response.data.user))
      .catch(err => {
        addToast({
          type: 'error',
          title: 'Erro na busca dos dados do usuário.',
          description: err?.response?.status,
        });
      });
  }, [addToast, id]);

  const getDues = useCallback(() => {
    setLoading(true);

    api
      .get(`dues/${id}`)
      .then(response => {
        setMembership(response.data.membership);
        setPayments(response.data.payments);

        if (response.data.membership) {
          setAmount(
            currencyMask(String(response.data.membership.amount * 100)),
          );
          setDueDay(String(response.data.membership.due_day));
        }
      })
      .catch(err => {
        addToast({
          type: 'error',
          title: 'Erro na busca da mensalidade.',
          description: err?.response?.status,
        });
      })
      .finally(() => setLoading(false));
  }, [addToast, id]);

  useEffect(() => {
    getUserData();
    getDues();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSave = useCallback(async () => {
    const parsedAmount = Number(currencyMask(amount, true));
    const parsedDueDay = Number(dueDay);

    if (Number.isNaN(parsedAmount) || parsedAmount < 0) {
      addToast({ type: 'error', title: 'Valor da mensalidade inválido.' });
      return;
    }

    if (Number.isNaN(parsedDueDay) || parsedDueDay < 1 || parsedDueDay > 28) {
      addToast({
        type: 'error',
        title: 'Dia de vencimento deve estar entre 1 e 28.',
      });
      return;
    }

    setSaving(true);

    try {
      const response = await api.put(`dues/${id}`, {
        amount: parsedAmount,
        due_day: parsedDueDay,
      });

      setMembership(response.data);

      addToast({ type: 'success', title: 'Mensalidade salva com sucesso.' });
    } catch (err: any) {
      addToast({
        type: 'error',
        title: 'Erro ao salvar mensalidade.',
        description: err?.response?.status,
      });
    } finally {
      setSaving(false);
    }
  }, [addToast, amount, dueDay, id]);

  const handleMarkPayment = useCallback(async () => {
    setMarking(true);

    try {
      await api.post(`dues/${id}/payments`, {});
      addToast({ type: 'success', title: 'Pagamento registrado.' });
      getDues();
    } catch (err: any) {
      addToast({
        type: 'error',
        title: 'Erro ao registrar pagamento.',
        description: err?.response?.status,
      });
    } finally {
      setMarking(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addToast, id]);

  const status = useMemo(() => {
    if (!membership) return null;

    const now = new Date();
    const currentCompetence = format(now, 'yyyy-MM-01');
    const paidThisMonth = payments.some(
      payment => payment.competence_date.slice(0, 10) === currentCompetence,
    );

    if (paidThisMonth) {
      return { tone: 'ok' as const, label: 'Em dia' };
    }

    const dueDateThisMonth = new Date(
      now.getFullYear(),
      now.getMonth(),
      membership.due_day,
    );

    // Mensalidade cadastrada depois do vencimento deste mês -- não dá
    // pra estar em débito de um ciclo anterior à própria matrícula
    // (mesma regra usada na lista de inadimplentes, ver
    // ShowOverdueStudentsService no servidor).
    const createdAfterDueDate =
      new Date(membership.created_at) > dueDateThisMonth;

    if (now > dueDateThisMonth && !createdAfterDueDate) {
      return { tone: 'overdue' as const, label: 'Atrasado' };
    }

    return { tone: 'neutral' as const, label: 'Aguardando pagamento do mês' };
  }, [membership, payments]);

  return (
    <Painel loading={loading}>
      <Container>
        <BackLink type="button" onClick={() => navigate(`/users/${id}`)}>
          <Icon name="arrow_back" size={16} color={colors.primary} />
          Detalhes do aluno
        </BackLink>

        <Header>
          <ContainerAvatar>
            <img src={user?.avatar_url || noAvatar} alt="avatar do aluno" />
          </ContainerAvatar>
          <div>
            <StudentName>
              Mensalidade de <span>{formatName(user?.fullname || '')}</span>
            </StudentName>
            <Subtitle>Cadastro e histórico de pagamento</Subtitle>
          </div>
        </Header>

        {status && (
          <StatusBanner tone={status.tone}>{status.label}</StatusBanner>
        )}

        <Card>
          <CardTitle>Valor e vencimento</CardTitle>
          <FieldsRow>
            <Field>
              <label htmlFor="amount">Mensalidade</label>
              <FieldInputGroup>
                <FieldUnit side="left">R$</FieldUnit>
                <FieldInput
                  id="amount"
                  type="text"
                  inputMode="decimal"
                  placeholder="150,00"
                  hasUnit="left"
                  value={amount}
                  onChange={e => setAmount(currencyMask(e.target.value))}
                />
              </FieldInputGroup>
            </Field>
            <Field>
              <label htmlFor="due_day">Dia do vencimento</label>
              <FieldInputGroup>
                <FieldInput
                  id="due_day"
                  type="number"
                  min={1}
                  max={28}
                  placeholder="10"
                  hasUnit="right"
                  value={dueDay}
                  onChange={e => setDueDay(e.target.value)}
                />
                <FieldUnit side="right">dia</FieldUnit>
              </FieldInputGroup>
            </Field>
          </FieldsRow>

          <ActionsRow>
            <PrimaryButton type="button" onClick={handleSave} disabled={saving}>
              {saving ? 'Salvando...' : 'Salvar mensalidade'}
            </PrimaryButton>
            {membership && (
              <SecondaryButton
                type="button"
                onClick={handleMarkPayment}
                disabled={marking}
              >
                {marking ? 'Registrando...' : 'Marcar pagamento deste mês'}
              </SecondaryButton>
            )}
          </ActionsRow>
        </Card>

        <Card>
          <CardTitle>Histórico de pagamentos</CardTitle>
          {payments.length === 0 ? (
            <EmptyState>Nenhum pagamento registrado ainda.</EmptyState>
          ) : (
            payments.map(payment => (
              <HistoryRow key={payment.id}>
                <span>
                  {format(
                    new Date(`${payment.competence_date}T12:00:00`),
                    'MMMM/yyyy',
                  )}
                </span>
                <span>
                  R$ {payment.amount.toFixed(2)} · pago em{' '}
                  {format(new Date(payment.paid_at), 'dd/MM/yyyy')}
                </span>
              </HistoryRow>
            ))
          )}
        </Card>
      </Container>
    </Painel>
  );
};

export { UserDues };
